import * as Sentry from '@sentry/react';
import { useMemo } from 'react';
import { useStrategyStore, useThroughputTabStore } from '../../../../../../store/strategy-store/strategy-store';
import { collectChronologicalDates } from '../main-section.helpers';
import { ChartDataItem } from './contributing-epics-chart.type';

/**
 * Hook that prepares data for the Contributing Epics chart
 *
 * Transforms the raw initiative data into a format suitable for the bar chart,
 * where each data point contains:
 * - date: The time period label
 * - [epicTitle]: The value for each selected epic
 *
 * @returns {ChartDataItem[] | []} An array of data points for the chart, where each item
 * contains a date and values for each selected epic
 */
const useChartData = (): ChartDataItem[] | [] => {
  const initiative = useStrategyStore((state) => state.initiativePerformance);
  const selectedEpics = useThroughputTabStore((state) => state.selectedEpics);
  const metric = useThroughputTabStore((state) => state.metric);

  const chartData = useMemo(() => {
    try {
      if (!initiative) {
        return [];
      }

      const dates = collectChronologicalDates(initiative.contributing_epics_over_time, metric);

      const data = dates.map((date) => {
        const dataEntries = selectedEpics.map((epic) => {
          return [epic.title, initiative.contributing_epics_over_time?.[epic.id]?.[metric]?.[date] ?? 0];
        });

        return {
          date,
          ...Object.fromEntries(dataEntries),
        };
      });

      return data;
    } catch (error) {
      Sentry.captureException(error);
      return [];
    }
  }, [initiative, selectedEpics, metric]);

  return chartData;
};

export { useChartData };
